import React, { createContext, useCallback, useContext, useReducer } from 'react';
import { PropTypes } from 'prop-types';
import companyFieldsReducer, { ACTIVE, DISABLED, HIDDEN, MODES, initializeState } from './CompanyFieldsReducer';

const CompanyFieldsContext = createContext();

export const CompanyFieldsProvider = ({
    children,
    isIndependent,
    isNewMember,
    viewerCompany,
    setFieldValue,
    setIsIndependent,
    setIsNewCompany,
    setTouched,
    touched,
    disableCompanySelection,
    isEventRegistration,
}) => {
    const [state, dispatch] = useReducer(
        companyFieldsReducer,
        initializeState({
            isIndependent,
            isNewMember,
            disableCompanySelection,
            isEventRegistration,
            viewerCompany,
        })
    );

    const isFieldHidden = useCallback(
        field => {
            if (state == null) return false;

            return state.fieldStates?.[field] === HIDDEN;
        },
        [state]
    );

    const isFieldActive = useCallback(
        field => {
            if (state == null) return false;

            return state.fieldStates?.[field] === ACTIVE;
        },
        [state]
    );

    const isFieldDisabled = useCallback(
        field => {
            if (state == null) return false;

            return state.fieldStates?.[field] === DISABLED;
        },
        [state]
    );

    const clearSearchAction = useCallback(() => {
        setFieldValue('companyName', '', false);
        setFieldValue('companyID', null, false);
        dispatch({
            type: 'CLEAR_SEARCH',
        });
    }, [setFieldValue, dispatch]);

    const setModeAction = useCallback(
        newMode => {
            if (newMode === MODES.NEW_COMPANY) {
                clearCompanyValues();
            }
            setIsNewCompany(newMode === MODES.NEW_COMPANY);
            setIsIndependent(false);
            dispatch({ type: 'SET_MODE', payload: newMode });
        },
        [clearCompanyValues, setIsIndependent, setIsNewCompany]
    );

    const clearCompanyValues = useCallback(() => {
        setFieldValue('companyName', '', false);
        setFieldValue('companyID', null, false);
        setTouched({ ...touched, ...{ newCompanyName: false } });
    }, [setFieldValue, setTouched, touched]);

    const toggleIndependentAction = useCallback(() => {
        if (!state.independent) {
            setFieldValue('companyName', '', false);
            setFieldValue('companyID', '', false);
            setFieldValue('personalResponsibility', '', false);
            setFieldValue('title', '', false);
        }
        setIsIndependent(!state.independent);
        setFieldValue('independent', !state.independent);
        dispatch({
            type: 'TOGGLE_INDEPENDENT',
        });
    }, [state.independent, setIsIndependent, setFieldValue]);

    const values = {
        state,
        clearSearchAction,
        toggleIndependentAction,
        setModeAction,
        isFieldDisabled,
        isFieldHidden,
        isFieldActive,
    };

    return <CompanyFieldsContext.Provider value={values}>{children}</CompanyFieldsContext.Provider>;
};

CompanyFieldsProvider.propTypes = {
    children: PropTypes.any,
    isNewMember: PropTypes.bool,
    disableCompanySelection: PropTypes.bool,
    isEventRegistration: PropTypes.bool,
    isIndependent: PropTypes.bool,
    setFieldValue: PropTypes.func,
    viewerCompany: PropTypes.bool,
    setIsIndependent: PropTypes.func,
    setIsNewCompany: PropTypes.func,
    touched: PropTypes.any,
    setTouched: PropTypes.func,
};

const useCompanyFieldsContext = () => {
    const context = useContext(CompanyFieldsContext);

    if (context === undefined) {
        throw new Error('useCompanyFieldsContext must be used within CompanyFieldsContext');
    }

    return context;
};

export default useCompanyFieldsContext;
